import React, { Suspense } from 'react';
import type { GetServerSidePropsContext } from 'next';

import FloatingWrapper from '~/components/FloatingWrapper';
import LoadingSpinnerCenter from '~/components/LoadingSpinnerCenter';
import ScrollTop from '~/components/ScrollTop';
import StickyCartBar from '~/components/StickyCartBar';
import { TrackPage } from '~/components/TrackEvent';
import { OFFERS_ROUTER } from '~/constants/router';
import { getSnowdonJwt } from '~/domains/auth/utils';
import BaseLayout from '~/layouts/BaseLayout';
import RightMenu from '~/layouts/RightMenu';
import { fetchOAIData } from '~/libs/openapi';
import { prefetchReactQueryData } from '~/libs/reactQuery';
import MarketCardFloating from '~/pages/app/components/MarketCardBannerWithFloatingContainer/MarketCardFloating';
import type { OAISchema } from '~/types/openapi';

import { ShowcaseContainer } from './components/ShowcaseContainer';
import { ShowcaseImageContainer } from './components/ShowcaseImageContainer';

type Props = {
  name?: string;
  description?: string;
  thumbnailImageUrl?: string;
  mainImageUrl?: string;
};

const ShowcaseDetailPage = ({
  name,
  description,
  thumbnailImageUrl,
  mainImageUrl,
}: Props) => {
  return (
    <>
      <BaseLayout title={name || '기획전'} right={<RightMenu />}>
        <Suspense fallback={<LoadingSpinnerCenter />}>
          <TrackPage page="캐시노트마켓/offers/showcase" />
          <ShowcaseImageContainer
            name={name}
            description={description}
            thumbnailImageUrl={thumbnailImageUrl}
            mainImageUrl={mainImageUrl}
          />
          <ShowcaseContainer />
        </Suspense>
        <Suspense fallback={null}>
          <FloatingWrapper hasStickyBar>
            <ScrollTop />
            <StickyCartBar
              placementName="마켓/쇼케이스/상품리스트"
              className="!-mr-0 !mb-0"
            />
          </FloatingWrapper>
          <MarketCardFloating pageName="마켓/쇼케이스/상품리스트" />
        </Suspense>
      </BaseLayout>
    </>
  );
};

export default ShowcaseDetailPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const token = getSnowdonJwt(context);
  if (!token) {
    return {
      props: {},
    };
  }

  const { code } = context.query;

  if (!code) {
    return {
      redirect: {
        description: '/app',
      },
    };
  }

  const fetchOptions = {
    headers: {
      'X-Client-Auth-Token': token,
    },
  };

  const [
    { dehydratedState },
    { count, thumbnailImageUrl, mainImageUrl, name, description },
  ] = await Promise.all([
    prefetchReactQueryData({
      queries: [
        [
          '/api/market-place/v4/showcases',
          {
            code,
          },
        ],
        [
          '/api/market-place/v1/placements/showcase-top-banners',
          {
            code,
          },
        ],
        '/api/market-place/v1/buyer',
      ],
      fetchOptions,
    }),
    fetchOAIData<OAISchema['MarketShowcaseProductsResponseV3']>({
      queryKey: '/api/market-place/v4/showcases',
      variables: { code, size: 1 },
      fetchOptions,
    }),
  ]);

  // 처음 진입시 showcase 노출 상품 자체가 없으면 redirect 처리함
  if (!count) {
    return {
      redirect: {
        destination: OFFERS_ROUTER.emptyShowcase,
        permanent: false,
      },
    };
  }

  return {
    props: {
      dehydratedState,
      thumbnailImageUrl: thumbnailImageUrl || '',
      mainImageUrl: mainImageUrl || '',
      name,
      description,
    },
  };
};
